import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import {
  getCsvReadyStatus,
  getCsvReadyToastMessage,
  isCsvReadyFailure,
  isCsvReadySuccess,
  resolveExportKey,
  socketErrorMessage,
  subscribeAccountantCsvReady,
} from "../utils/csvExportSocket";

const CsvExportSocketContext = createContext(null);

export const useCsvExportSocket = () => {
  const ctx = useContext(CsvExportSocketContext);
  if (!ctx) {
    throw new Error("useCsvExportSocket must be used within CsvExportSocketProvider");
  }
  return ctx;
};

export const CsvExportSocketProvider = ({ children }) => {
  const handlersRef = useRef(new Map());
  const pendingRef = useRef(new Set());
  const notifiedRef = useRef(new Set());
  const [pendingKeys, setPendingKeys] = useState([]);

  const syncPending = useCallback(() => {
    setPendingKeys(Array.from(pendingRef.current));
  }, []);

  const trackExport = useCallback(
    (exportKey) => {
      if (!exportKey) return;
      notifiedRef.current.delete(exportKey);
      pendingRef.current.add(exportKey);
      syncPending();
    },
    [syncPending]
  );

  const clearExport = useCallback(
    (exportKey) => {
      if (!exportKey) return;
      pendingRef.current.delete(exportKey);
      syncPending();
    },
    [syncPending]
  );

  const registerExportHandler = useCallback((exportKey, handler) => {
    if (!exportKey) return () => {};
    handlersRef.current.set(exportKey, handler);
    return () => {
      if (handlersRef.current.get(exportKey) === handler) {
        handlersRef.current.delete(exportKey);
      }
    };
  }, []);

  const isExportPending = useCallback(
    (exportKey) => pendingKeys.includes(exportKey),
    [pendingKeys]
  );

  const finishExport = useCallback(
    (exportKey, { payload, toastMessage } = {}) => {
      if (!exportKey) return;

      clearExport(exportKey);
      handlersRef.current.get(exportKey)?.onComplete?.();

      if (notifiedRef.current.has(exportKey)) return;
      notifiedRef.current.add(exportKey);

      const message =
        toastMessage || getCsvReadyToastMessage(payload, { success: true });
      toast.success(message);
    },
    [clearExport]
  );

  const failExport = useCallback(
    (exportKey, message, payload) => {
      if (!exportKey) return;

      clearExport(exportKey);
      handlersRef.current.get(exportKey)?.onError?.();

      if (notifiedRef.current.has(exportKey)) return;
      notifiedRef.current.add(exportKey);

      const errMsg =
        message ||
        socketErrorMessage(payload) ||
        getCsvReadyToastMessage(payload, { success: false });
      toast.error(errMsg);
    },
    [clearExport]
  );

  useEffect(() => {
    const token = localStorage.getItem("sessionToken");
    if (!token) return undefined;

    const handleCsvReady = (payload) => {
      const pendingKeys = Array.from(pendingRef.current);
      let exportKey = resolveExportKey(payload, { pendingKeys });
      if (!exportKey && pendingKeys.length === 1) {
        exportKey = pendingKeys[0];
      }

      const status = getCsvReadyStatus(payload);
      console.log("[CSV Export Socket] accountant_csv_ready", {
        payload,
        status,
        exportKey,
        pendingKeys,
        isSuccess: isCsvReadySuccess(payload),
        isFailure: isCsvReadyFailure(payload),
      });

      if (isCsvReadyFailure(payload)) {
        const message =
          socketErrorMessage(payload) || getCsvReadyToastMessage(payload, { success: false });
        if (exportKey) {
          failExport(exportKey, message, payload);
        } else {
          pendingRef.current.clear();
          syncPending();
          toast.error(message);
        }
        return;
      }

      if (!isCsvReadySuccess(payload)) return;

      if (exportKey) {
        finishExport(exportKey, { payload });
      }
    };

    return subscribeAccountantCsvReady(handleCsvReady);
  }, [finishExport, failExport, syncPending]);

  const value = useMemo(
    () => ({
      trackExport,
      clearExport,
      registerExportHandler,
      isExportPending,
      finishExport,
      failExport,
    }),
    [trackExport, clearExport, registerExportHandler, isExportPending, finishExport, failExport]
  );

  return (
    <CsvExportSocketContext.Provider value={value}>{children}</CsvExportSocketContext.Provider>
  );
};
